import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format, differenceInCalendarDays } from "date-fns";
import { Loader2, X, ArrowDown, ArrowUp, Minus, Flame, BarChart3 } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const KG_PER_LB = 0.45359237;
const CM_PER_INCH = 2.54;

/**
 * Read the user's measurement preferences. We honour the boolean fields set
 * by the Settings page (`use_lbs`, `use_inches`) and fall back to imperial.
 */
function getUnitPrefs(user) {
  const useLbs = user?.use_lbs ?? true; // true = lbs, false = kg
  const useInches = user?.use_inches ?? true; // true = inches, false = cm
  return {
    weightUnit: useLbs ? "lbs" : "kg",
    lengthUnit: useInches ? "in" : "cm",
    useLbs,
    useInches,
  };
}

/**
 * Subtle quick-log button on the Hub. Lets users record weight, BF%, and
 * waist measurement on the fly. The chip to the left shows the user's
 * weight delta since their previous progress log.
 */
export default function QuickProgressLog() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const prefs = getUnitPrefs(user);

  const { data: logs = [] } = useQuery({
    queryKey: ["progress"],
    queryFn: () => base44.entities.ProgressLog.list("-date", 30),
    enabled: !!user,
  });

  // Same-date logs aren't ordered deterministically by `-date` alone, so we
  // re-sort newest-first using (date, created_date) as a tiebreaker.
  const sortedLogs = [...logs].sort((a, b) => {
    const dateCmp = (b.date || "").localeCompare(a.date || "");
    if (dateCmp !== 0) return dateCmp;
    return (b.created_date || "").localeCompare(a.created_date || "");
  });

  // Pull the two most-recent entries that have a weight reading so we can
  // show a delta. Logs are sorted newest-first above.
  const weightLogs = sortedLogs.filter((l) => typeof l.weight_kg === "number" && !Number.isNaN(l.weight_kg));
  const latest = weightLogs[0];
  const previous = weightLogs[1];

  let delta = null;
  if (latest && previous) {
    const diffKg = latest.weight_kg - previous.weight_kg;
    const diff = prefs.useLbs ? diffKg / KG_PER_LB : diffKg;
    const dayGap = (() => {
      try {
        return Math.abs(differenceInCalendarDays(new Date(latest.date), new Date(previous.date)));
      } catch (_e) {
        return null;
      }
    })();
    delta = {
      value: diff,
      unit: prefs.weightUnit,
      direction: Math.abs(diff) < 0.05 ? "flat" : diff < 0 ? "down" : "up",
      dayGap,
    };
  }

  return (
    <>
      <div className="flex items-center gap-1.5 shrink-0" data-testid="quick-progress-log">
        {delta && <DeltaChip delta={delta} />}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/5 hover:bg-sky-500/20 border border-white/10 text-white hover:text-sky-300 font-bold text-xs transition active:scale-95 cursor-pointer shadow-sm"
          data-testid="quick-progress-log-btn"
        >
          <BarChart3 className="w-3.5 h-3.5 text-sky-400 shrink-0" />
          <span className="whitespace-nowrap">Log Progress</span>
        </button>
      </div>
      {open && (
        <QuickProgressDialog open={open} onOpenChange={setOpen} user={user} />
      )}
    </>
  );
}

function DeltaChip({ delta }) {
  // Visual: red(down)/green(up)/grey(flat). Fitness convention varies — we
  // treat "down" as positive (cut/maintenance progress) and "up" as caution
  // colour, with a flat indicator for ~0 change. Either way the arrow makes
  // the direction unambiguous.
  const isDown = delta.direction === "down";
  const isFlat = delta.direction === "flat";
  const Icon = isFlat ? Minus : isDown ? ArrowDown : ArrowUp;
  const valueTone = isFlat
    ? "text-white/70"
    : isDown
    ? "text-emerald-300"
    : "text-amber-300";

  const abs = Math.abs(delta.value);
  const formatted = abs >= 10 ? abs.toFixed(0) : abs.toFixed(1);
  const labelSuffix =
    delta.dayGap != null
      ? delta.dayGap === 0
        ? "today"
        : `${delta.dayGap}d ago`
      : "last log";

  return (
    <div
      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-white/5 border border-white/10"
      title={`Weight change since your previous log${delta.dayGap != null ? ` (${delta.dayGap} day${delta.dayGap === 1 ? "" : "s"} ago)` : ""}`}
      data-testid="quick-progress-delta-chip"
    >
      <Flame className="w-3.5 h-3.5 text-orange-400 shrink-0" fill="currentColor" />
      <div className="flex flex-col leading-tight">
        <span className={`text-xs font-bold flex items-center gap-1 ${valueTone}`}>
          <Icon className="w-3 h-3" />
          {isFlat ? "0" : formatted} {delta.unit}
        </span>
        <span className="text-[9px] text-white/50">{labelSuffix}</span>
      </div>
    </div>
  );
}

function QuickProgressDialog({ open, onOpenChange, user }) {
  const queryClient = useQueryClient();
  const { weightUnit, lengthUnit, useLbs, useInches } = getUnitPrefs(user);

  const [weight, setWeight] = useState("");
  const [bodyFat, setBodyFat] = useState("");
  const [waist, setWaist] = useState("");
  const [notes, setNotes] = useState("");

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.ProgressLog.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["progress"] });
      toast.success("Progress logged.");
      onOpenChange(false);
    },
    onError: (err) =>
      toast.error(err?.response?.data?.detail || "Could not save progress log."),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = { date: format(new Date(), "yyyy-MM-dd") };

    if (weight !== "") {
      const w = parseFloat(weight);
      if (!Number.isNaN(w) && w > 0) {
        payload.weight_kg = useLbs ? w * KG_PER_LB : w;
      }
    }
    if (bodyFat !== "") {
      const b = parseFloat(bodyFat);
      if (!Number.isNaN(b) && b >= 0) payload.body_fat_percentage = b;
    }
    if (waist !== "") {
      const wa = parseFloat(waist);
      if (!Number.isNaN(wa) && wa > 0) {
        payload.waist_cm = useInches ? wa * CM_PER_INCH : wa;
      }
    }
    if (notes.trim()) payload.notes = notes.trim();

    if (
      payload.weight_kg === undefined &&
      payload.body_fat_percentage === undefined &&
      payload.waist_cm === undefined
    ) {
      toast.error("Enter at least one measurement.");
      return;
    }
    createMutation.mutate(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="quick-progress-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Log progress</DialogTitle>
          <DialogDescription>
            Quick check-in. Saved to your Progress history.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="qp-weight">Weight ({weightUnit})</Label>
            <Input
              id="qp-weight"
              data-testid="qp-weight-input"
              type="number"
              inputMode="decimal"
              step="0.1"
              placeholder={useLbs ? "165.0" : "75.0"}
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="qp-bf">Body fat (%)</Label>
              <Input
                id="qp-bf"
                data-testid="qp-bf-input"
                type="number"
                inputMode="decimal"
                step="0.1"
                placeholder="15.0"
                value={bodyFat}
                onChange={(e) => setBodyFat(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="qp-waist">Waist ({lengthUnit})</Label>
              <Input
                id="qp-waist"
                data-testid="qp-waist-input"
                type="number"
                inputMode="decimal"
                step="0.1"
                placeholder={useInches ? "32" : "82"}
                value={waist}
                onChange={(e) => setWaist(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="qp-notes">Notes (optional)</Label>
            <Input
              id="qp-notes"
              data-testid="qp-notes-input"
              placeholder="How you're feeling, training, etc."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => onOpenChange(false)}
              disabled={createMutation.isPending}
              data-testid="qp-cancel-btn"
            >
              <X className="w-4 h-4 mr-1.5" /> Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 gap-2"
              disabled={createMutation.isPending}
              data-testid="qp-save-btn"
            >
              {createMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Save log
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export { QuickProgressDialog };
